import { StaticImport } from 'next/dist/shared/lib/get-img-props';
import Image from 'next/image';
import { type ComponentProps, type ReactNode, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useUser from '@alltrails/context/hooks/useUser';
import { BREAKPOINT_SM_SIZE } from '@alltrails/denali/tokens';
import useExposureEvent from '@alltrails/experiments/hooks/useExposureEvent';
import type { SubscriptionFeatures } from '@alltrails/types/SubscriptionFeatures';
import type { Upsell } from '@alltrails/types/Upsell';
import BaseUpsellModal from '../BaseUpsell/BaseUpsellModal';
import GenericUpsellContent from '../GenericUpsellContent/GenericUpsellContent';
import getServerUpsellCopy from '../getServerUpsellCopy';
import getUpsellCtaText from '../getUpsellCtaText';
import defaultHeaderImageSrc from './images/route-assistant-web.avif';

export const peakUpsellHeading = <FormattedMessage defaultMessage="Backpacking? Try Peak." />;
export const peakUpsellPromoEligibleHeading = <FormattedMessage defaultMessage="Backpacking? Try Peak. Your first week’s on us." />;

type Props = {
  ctaText?: ReactNode;
  headerImageSrc?: StaticImport;
  heading?: ReactNode;
  isCrossPlatform?: ComponentProps<typeof BaseUpsellModal>['isCrossPlatform'];
  isOpen: boolean;
  onCloseRequest: ComponentProps<typeof BaseUpsellModal>['onCloseRequest'];
  onCrossPlatformRequest?: ComponentProps<typeof BaseUpsellModal>['onCrossPlatformRequest'];
  onSuccess?: ComponentProps<typeof BaseUpsellModal>['onSuccess'];
  promoEligibleCtaText?: ReactNode;
  promoEligibleHeading?: ReactNode;
  promoEligibleSubCopy?: ReactNode;
  promoEligibleSubheading?: ReactNode;
  // Server-driven copy for this trigger. Each field it provides wins over the props above.
  serverUpsellContent?: Upsell;
  // When true, the CTA skips checkout and fires onSuccess directly (used by the Peak-to-Plus downgrade flow).
  skipCheckout?: boolean;
  subCopy?: ReactNode;
  subheading?: ReactNode;
  subscriptionFeatures?: SubscriptionFeatures;
  upsellTrigger: UpsellTrigger;
};

// Non-tiered Peak upsell modal. Design: https://www.figma.com/design/5lZfiEveGScwx75JKpB12W/Upsell-XPs-2026?node-id=5340-39148&m=dev
export default function GenericPeakUpsell({
  ctaText,
  headerImageSrc = defaultHeaderImageSrc,
  heading = peakUpsellHeading,
  isCrossPlatform,
  isOpen,
  onCloseRequest,
  onCrossPlatformRequest,
  onSuccess,
  promoEligibleCtaText,
  promoEligibleHeading = peakUpsellPromoEligibleHeading,
  promoEligibleSubCopy,
  promoEligibleSubheading,
  serverUpsellContent,
  skipCheckout,
  subCopy,
  subheading,
  subscriptionFeatures,
  upsellTrigger
}: Props) {
  const user = useUser();
  const isPromoEligible = user?.isPromoEligible || false;
  const sendUpsellExposure = useExposureEvent(serverUpsellContent?.experimentKey);

  // Sent here rather than by the opening surface, so the exposure reflects the copy actually shown.
  useEffect(() => {
    if (isOpen && serverUpsellContent?.experimentKey) {
      sendUpsellExposure();
    }
  }, [isOpen, sendUpsellExposure, serverUpsellContent?.experimentKey]);

  const serverCopy = getServerUpsellCopy({ isPromoEligible, serverUpsellContent });

  const resolvedCtaText = serverCopy.ctaText || (isPromoEligible ? promoEligibleCtaText : ctaText);
  const resolvedSubCopy = serverCopy.subCopy || (isPromoEligible ? promoEligibleSubCopy : subCopy);

  return (
    <BaseUpsellModal
      caption={resolvedSubCopy}
      headerImage={<Image alt="" sizes={`(max-width: ${BREAKPOINT_SM_SIZE}px) 100vw, 480px`} src={headerImageSrc} />}
      headerImageV3
      heading={serverCopy.heading || heading}
      isCrossPlatform={isCrossPlatform}
      isOpen={isOpen}
      onCloseRequest={onCloseRequest}
      onCrossPlatformRequest={onCrossPlatformRequest}
      onSuccess={onSuccess}
      primaryButtonText={resolvedCtaText ?? getUpsellCtaText({ tier: 'peak', user })}
      primaryButtonVariant="accent"
      productsAvailable="peak"
      promoEligibleHeading={serverCopy.promoEligibleHeading || promoEligibleHeading}
      purchaseTier="peak"
      skipCheckout={skipCheckout}
      tallerOnMobile
      upsellTrigger={upsellTrigger}
    >
      <GenericUpsellContent
        heading={serverCopy.heading || heading}
        isPromoEligible={isPromoEligible}
        promoEligibleHeading={serverCopy.promoEligibleHeading || promoEligibleHeading}
        promoEligibleSubheading={serverCopy.promoEligibleSubheading || promoEligibleSubheading}
        subheading={serverCopy.subheading || subheading}
        subscriptionFeatures={serverCopy.subscriptionFeatures || subscriptionFeatures}
        tier="peak"
        upsellTrigger={upsellTrigger}
      />
    </BaseUpsellModal>
  );
}
